'use client';

import { Box, Flex, Typography } from '@coolblue-development/freddy';
import selectLeaseTenureTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/selectLeaseTenureTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { SelectableBox, SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import { useSearchParams } from 'next/navigation';
import { useContext } from 'react';

import { LeaseContext } from './Context';
import ModalButton from './ModalButton';

const LeaseTenureButtons = () => {
  const { t } = useTranslation('webshop.business.leasing.leaseTenureButtons');
  const { trackEvent } = useAnalytics();
  const searchParams = useSearchParams();
  const testId = useTestId('lease-tenure-buttons');

  const isLeasePage = searchParams.get('lease') === '1';

  const { selectedTermId, setSelectedTermId, leaseTerms } = useContext(LeaseContext);

  const handleSelect = (id: string) => {
    setSelectedTermId(id);
    const duration = leaseTerms?.find(term => term.id === id)?.duration || 36;

    trackEvent(
      selectLeaseTenureTrigger({
        content: `${duration as 36 | 24 | 12 | 6}m`
      })
    );
  };

  if (!isLeasePage) {
    return <></>;
  }

  return (
    <Box mt={500}>
      <Flex direction='row' alignItems='start' gap={100}>
        <Typography fontWeight={600} mr={100} mb={200}>
          {t('label')}
        </Typography>
        <ModalButton />
      </Flex>
      <Flex direction='row' alignItems='center' gap={200} mb={200} role='radiogroup' {...testId}>
        <SelectableBoxGroup
          name='lease-tenure-buttons'
          type='radio'
          spacing='compact'
          inputPlacement='hidden'
          value={selectedTermId}
        >
          {leaseTerms?.map(term => (
            <SelectableBox key={term.id} value={term.id} onClick={() => handleSelect(term.id)}>
              <Flex
                direction='row'
                alignItems='center'
                sx={{
                  height: '20px'
                }}
              >
                <Typography component='span' mr={100}>
                  {selectedTermId === term.id
                    ? `${leaseTerms.find(_term => _term.id === term.id)?.duration} ` + t('duration')
                    : `${leaseTerms.find(_term => _term.id === term.id)?.duration}m`}
                </Typography>
              </Flex>
            </SelectableBox>
          ))}
        </SelectableBoxGroup>
      </Flex>
    </Box>
  );
};

export default LeaseTenureButtons;
